'use client';

import { Field, Label, Radio, RadioGroup, Switch } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { ArrowDownRightIcon, ArrowUpRightIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { ReactNode, useEffect, useState } from 'react';
import ModalForm from '../Modal/Modal';
import RichText from '../RichText';
import React from 'react';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import CustomRichText from '../CustomRichText';
import { useCurrentLocale } from '@/utils/hooks/useCurrentLocale';
import { localeText } from '@/utils/constantTxt/localeText';

type FrequencyType = 'monthly' | 'halfyear' | 'annually'; // Types for frequency options

type PriceType = {
  monthly: string;
  halfyear: string;
  annually: string;
};

type DescriptionType = {
  monthly: string;
  halfyear: string;
  annually: string;
};

type FeatureItem = {
  name: React.JSX.Element;
  enters: boolean;
};

type FeatureType = {
  [key in FrequencyType]: FeatureItem;
};

type PlanType = {
  name: string;
  id: string;
  price: PriceType;
  priceWithPersonal: PriceType;
  description: DescriptionType;
  descriptionWithPersonal: DescriptionType;
  features: FeatureType[];
  featuresWithPersonal: FeatureType[];
  superPrice: boolean;
};

type VipType = {
  name: string;
  id: string;
  price: string;
  priceWithPersonal: string;
  description: string;
  descriptionWithPersonal: string;
  features: Array<{
    vip: {
      name: React.JSX.Element;
      enters: boolean;
    };
  }>;
  featuresWithPersonal: Array<{
    vip: {
      name: React.JSX.Element;
      enters: boolean;
    };
  }>;
  superPrice: boolean;
};

type TFrequencies = {
  value: FrequencyType | 'vip';
  label: string;
  priceSuffix: string;
  discount?: string;
};
const defaultTiers: PlanType[] = [
  {
    name: '1 предмет',
    id: 'tier-1',
    price: {
      monthly: '77',
      halfyear: '77',
      annually: '77',
    },
    priceWithPersonal: {
      monthly: '220',
      halfyear: '220',
      annually: '220',
    },
    description: {
      monthly: '1 834 грн (за 3 місяці)',
      halfyear: '3 667 грн (за півроку)',
      annually: '5500 грн (за пакет)',
    },
    descriptionWithPersonal: {
      monthly: '6 600 грн (за 3 місяці)',
      halfyear: '13 200 грн (за півроку)',
      annually: ' 19 800 грн (за пакет)',
    },
    features: [
      {
        monthly: {
          name: (
            <p>
              по <span className="font-bold">2</span> уроки на тиждень
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              по <span className="font-bold">2</span> уроки на тиждень
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              по <span className="font-bold">2</span> уроки на тиждень
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <p>
              <span className="font-bold">24</span> уроки — теоретичних і практичних по 60 хв
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              <span className="font-bold">48</span> уроки — теоретичних і практичних по 60 хв
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              <span className="font-bold">72</span> уроки — теоретичних і практичних по 60 хв
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        annually: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        annually: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        annually: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        halfyear: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        annually: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
      },
    ],
    featuresWithPersonal: [
      {
        monthly: {
          name: (
            <p>
              по <span className="font-bold">2</span> уроки на тиждень
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              по <span className="font-bold">2</span> уроки на тиждень
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              по <span className="font-bold">2</span> уроки на тиждень
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <p>
              <span className="font-bold">30</span> уроків:
              <span className="font-bold">24</span> - теоретичні і практичні по 60 хв +{' '}
              <span className="font-bold">6</span> індивідуальні по 60 хв
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              <span className="font-bold">60</span> уроків:
              <span className="font-bold">48</span> - теоретичні і практичні по 60 хв +{' '}
              <span className="font-bold">12</span> індивідуальні по 60 хв
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              <span className="font-bold">90</span> уроків:
              <span className="font-bold">72</span> - теоретичні і практичні по 60 хв +{' '}
              <span className="font-bold">18</span> індивідуальні по 60 хв
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        annually: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        annually: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        annually: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        halfyear: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        annually: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
      },
    ],
    superPrice: false,
  },
  {
    name: '2 предмети',
    id: 'tier-2',
    price: {
      monthly: '77',
      halfyear: '77',
      annually: '77',
    },
    priceWithPersonal: {
      monthly: '155',
      halfyear: '155',
      annually: '155',
    },
    description: {
      monthly: '3 667 грн (за 3 місяці)',
      halfyear: '7 334 грн (за півроку)',
      annually: '11000 грн (за пакет)',
    },
    descriptionWithPersonal: {
      monthly: '9 267 грн (за 3 місяці)',
      halfyear: '18 534 грн (за півроку)',
      annually: '27 800 грн (за пакет)',
    },
    features: [
      {
        monthly: {
          name: (
            <p>
              по <span className="font-bold">4</span> уроки на тиждень
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              по <span className="font-bold">4</span> уроки на тиждень
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              по <span className="font-bold">4</span> уроки на тиждень
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <p>
              <span className="font-bold">48</span> уроки — теоретичних і практичних по 60 хв
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              <span className="font-bold">96</span> уроки — теоретичних і практичних по 60 хв
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              <span className="font-bold">144</span> уроки — теоретичних і практичних по 60 хв
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        annually: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        annually: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        annually: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        halfyear: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        annually: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
      },
    ],
    featuresWithPersonal: [
      {
        monthly: {
          name: (
            <p>
              по <span className="font-bold">4</span> уроки на тиждень
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              по <span className="font-bold">4</span> уроки на тиждень
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              по <span className="font-bold">4</span> уроки на тиждень
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <p>
              <span className="font-bold">60</span> уроків:
              <span className="font-bold">48</span> - теоретичні і практичні по 60 хв +{' '}
              <span className="font-bold">12</span> індивідуальні по 60 хв
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              <span className="font-bold">120</span> уроків:
              <span className="font-bold">96</span> - теоретичні і практичні по 60 хв +{' '}
              <span className="font-bold">24</span> індивідуальні по 60 хв
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              <span className="font-bold">180</span> уроків:
              <span className="font-bold">144</span> - теоретичні і практичні по 60 хв +{' '}
              <span className="font-bold">36</span> індивідуальні по 60 хв
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        annually: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        annually: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        annually: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        halfyear: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        annually: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
      },
    ],
    superPrice: false,
  },
  {
    name: '3 предмети',
    id: 'tier-3',
    price: {
      monthly: '77',
      halfyear: '77',
      annually: '77',
    },
    priceWithPersonal: {
      monthly: '133',
      halfyear: '133',
      annually: '133',
    },
    description: {
      monthly: '5 500 грн (за 3 місяці)',
      halfyear: '11 000 грн (за півроку)',
      annually: '16 500 грн (за пакет)',
    },
    descriptionWithPersonal: {
      monthly: '11 934 грн (за 3 місяці)',
      halfyear: '23 867 грн (за півроку)',
      annually: '35 800 грн (за пакет)',
    },
    features: [
      {
        monthly: {
          name: (
            <p>
              по <span className="font-bold">6</span> уроків на тиждень
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              по <span className="font-bold">6</span> уроків на тиждень
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              по <span className="font-bold">6</span> уроків на тиждень
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <p>
              <span className="font-bold">72</span> уроки — теоретичних і практичних по 60 хв
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              <span className="font-bold">144</span> уроки — теоретичних і практичних по 60 хв
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              <span className="font-bold">216</span> уроки — теоретичних і практичних по 60 хв
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        annually: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        annually: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        annually: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        halfyear: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        annually: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
      },
    ],
    featuresWithPersonal: [
      {
        monthly: {
          name: (
            <p>
              по <span className="font-bold">6</span> уроків на тиждень
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              по <span className="font-bold">6</span> уроків на тиждень
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              по <span className="font-bold">6</span> уроків на тиждень
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <p>
              <span className="font-bold">90</span> уроків:
              <span className="font-bold">72</span> - теоретичні і практичні по 60 хв +{' '}
              <span className="font-bold">18</span> індивідуальні по 60 хв
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              <span className="font-bold">180</span> уроків:
              <span className="font-bold">144</span> - теоретичні і практичні по 60 хв +{' '}
              <span className="font-bold">36</span> індивідуальні по 60 хв
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              <span className="font-bold">270</span> уроків:
              <span className="font-bold">216</span> - теоретичні і практичні по 60 хв +{' '}
              <span className="font-bold">54</span> індивідуальні по 60 хв
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        annually: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        annually: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        annually: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        halfyear: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        annually: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: true,
        },
      },
    ],
    superPrice: false,
  },
  {
    name: '4 предмети',
    id: 'tier-4',
    price: {
      monthly: '77',
      halfyear: '77',
      annually: '77',
    },
    priceWithPersonal: {
      monthly: '124',
      halfyear: '124',
      annually: '124',
    },
    description: {
      monthly: '7 334 грн (за 3 місяці)',
      halfyear: '14 667 грн (за півроку)',
      annually: ' 22 000 грн (за пакет)',
    },
    descriptionWithPersonal: {
      monthly: '14 800 грн (за 3 місяці)',
      halfyear: '29 600 грн (за півроку)',
      annually: '44 400 грн (за пакет)',
    },
    features: [
      {
        monthly: {
          name: (
            <p>
              по <span className="font-bold">8</span> уроків на тиждень
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              по <span className="font-bold">8</span> уроків на тиждень
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              по <span className="font-bold">8</span> уроків на тиждень
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <p>
              <span className="font-bold">96</span> уроки — теоретичних і практичних по 60 хв
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              <span className="font-bold">192</span> уроки — теоретичних і практичних по 60 хв
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              <span className="font-bold">288</span> уроки — теоретичних і практичних по 60 хв
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        annually: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        annually: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        annually: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        halfyear: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        annually: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
      },
    ],
    featuresWithPersonal: [
      {
        monthly: {
          name: (
            <p>
              по <span className="font-bold">8</span> уроків на тиждень
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              по <span className="font-bold">8</span> уроків на тиждень
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              по <span className="font-bold">8</span> уроків на тиждень
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <p>
              <span className="font-bold">120</span> уроків:
              <span className="font-bold">96</span> - теоретичні і практичні по 60 хв +{' '}
              <span className="font-bold">24</span> індивідуальні по 60 хв
            </p>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <p>
              <span className="font-bold">240</span> уроків:
              <span className="font-bold">192</span> - теоретичні і практичні по 60 хв +{' '}
              <span className="font-bold">48</span> індивідуальні по 60 хв
            </p>
          ),
          enters: true,
        },
        annually: {
          name: (
            <p>
              <span className="font-bold">360</span> уроків:
              <span className="font-bold">288</span> - теоретичні і практичні по 60 хв +{' '}
              <span className="font-bold">72</span> індивідуальні по 60 хв
            </p>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        halfyear: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
        annually: {
          name: (
            <div>
              <p>Предмети на вибір:</p>
              <ul className="list-disc pl-8">
                <li>Англійська мова</li>
                <li>Німецька мова</li>
                <li>Українська мова</li>
                <li>Математика</li>
                <li>Історія України</li>
              </ul>
            </div>
          ),
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
        annually: {
          name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        halfyear: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
        annually: {
          name: <p>Постійна підтримка викладача</p>,
          enters: true,
        },
      },
      {
        monthly: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        halfyear: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: false,
        },
        annually: {
          name: <p>Ноутбук для навчання в подарунок</p>,
          enters: true,
        },
      },
    ],
    superPrice: false,
  },
];

const vipTier: VipType = {
  id: 'vip-tier',
  name: 'VIP',
  price: '162',
  priceWithPersonal: '192',
  description: '46 000 грн (за пакет)',
  descriptionWithPersonal: '68 900 грн (за пакет)',
  features: [
    {
      vip: {
        name: (
          <p>
            по <span className="font-bold">8</span> уроків на тиждень
          </p>
        ),
        enters: true,
      },
    },
    {
      vip: {
        name: (
          <p>
            <span className="font-bold">288</span> уроки — теоретичних і практичних по 60 хв
          </p>
        ),
        enters: true,
      },
    },
    {
      vip: {
        name: (
          <div>
            <p>4 Предмети на вибір:</p>
            <ul className="list-disc pl-8">
              <li>Англійська мова</li>
              <li>Німецька мова</li>
              <li>Українська мова</li>
              <li>Математика</li>
              <li>Історія України</li>
            </ul>
          </div>
        ),
        enters: true,
      },
    },
    {
      vip: {
        name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
        enters: true,
      },
    },
    {
      vip: {
        name: <p>Постійна підтримка викладача</p>,
        enters: true,
      },
    },
    {
      vip: {
        name: <p>Технічна підтримка</p>,
        enters: true,
      },
    },
    {
      vip: {
        name: <p>Ноутбук для навчання в подарунок</p>,
        enters: true,
      },
    },
    {
      vip: {
        name: <p>Гарантія вступу до ВНЗ на державну форму навчання</p>,
        enters: true,
      },
    },
  ],
  featuresWithPersonal: [
    {
      vip: {
        name: (
          <p>
            по <span className="font-bold">8</span> уроків на тиждень
          </p>
        ),
        enters: true,
      },
    },
    {
      vip: {
        name: (
          <p>
            <span className="font-bold">360</span> уроків:
            <span className="font-bold">288</span> - теоретичні і практичні по 60 хв +{' '}
            <span className="font-bold">72</span> індивідуальні по 60 хв
          </p>
        ),
        enters: true,
      },
    },
    {
      vip: {
        name: (
          <div>
            <p>4 Предмети на вибір:</p>
            <ul className="list-disc pl-8">
              <li>Англійська мова</li>
              <li>Німецька мова</li>
              <li>Українська мова</li>
              <li>Математика</li>
              <li>Історія України</li>
            </ul>
          </div>
        ),
        enters: true,
      },
    },
    {
      vip: {
        name: <p>Доступ до записів занять та інтерактивних матеріалів</p>,
        enters: true,
      },
    },
    {
      vip: {
        name: <p>Постійна підтримка викладача</p>,
        enters: true,
      },
    },
    {
      vip: {
        name: <p>Особистий куратор</p>,
        enters: true,
      },
    },
    {
      vip: {
        name: <p>Контроль відвідуваності</p>,
        enters: true,
      },
    },
    {
      vip: {
        name: <p>Ноутбук для навчання в подарунок</p>,
        enters: true,
      },
    },
    {
      vip: {
        name: <p>Гарантія вступу до ВНЗ на державну форму навчання</p>,
        enters: true,
      },
    },
  ],
  superPrice: false,
};

function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(' ');
}

export interface PriceBlockNMTProps {
  id?: string;
  title?: ReactNode | SerializedEditorState;
  subtitle?: ReactNode | SerializedEditorState;
  useDefaultTires?: boolean;
  tiers?: Array<{
    id: string;
    name: string;
    price: {
      monthly: string;
      halfyear: string;
      annually: string;
    };
    priceWithPersonal: {
      monthly: string;
      halfyear: string;
      annually: string;
    };
    description: { [key: string]: ReactNode | SerializedEditorState };
    descriptionWithPersonal: { [key: string]: ReactNode | SerializedEditorState };
    features: Array<{
      [key: string]: { name: ReactNode | SerializedEditorState; enters: boolean };
    }>;
    featuresWithPersonal: Array<{
      [key: string]: { name: ReactNode | SerializedEditorState; enters: boolean };
    }>;
    superPrice: boolean;
  }>;
  individualLessonsLabel?: string;
  selectPackageLabel?: string;
  viewPackageLabel?: string;
  collapseLabel?: string;
  modalTagPrefix?: string;
}

export default function PriceBlockNMT({
  id = 'price',
  title = 'Оберіть свій пакет підготовки до НМТ',
  subtitle = 'Всі пакети містять доступ до записів занять, інтерактивних матеріалів та постійну підтримку від вчителя',
  useDefaultTires = true,
  tiers = defaultTiers,
  individualLessonsLabel = 'Індивідуальні заняття',
  selectPackageLabel = 'Обрати пакет',
  viewPackageLabel = 'Переглянути пакет',
  collapseLabel = 'Згорнути',
  modalTagPrefix = 'НМТ',
}: PriceBlockNMTProps) {
  const loc = useCurrentLocale();

  const frequencyOptions: TFrequencies[] = [
    {
      value: 'monthly',
      label: localeText[loc].threeMonth,
      priceSuffix: localeText[loc].uahLesson,
    },
    {
      value: 'halfyear',
      label: localeText[loc].halfYear,
      priceSuffix: localeText[loc].uahLesson,
    },
    {
      value: 'annually',
      label: localeText[loc].year,
      priceSuffix: localeText[loc].uahLesson,
    },
  ];
  const [dispayedTiers] = useState(useDefaultTires || !tiers?.length ? defaultTiers : tiers);
  const [frequency, setFrequency] = useState(frequencyOptions[0]);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<(typeof tiers)[0] | typeof vipTier | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<(typeof tiers)[0] | typeof vipTier | null>(
    null,
  );
  const [enabled, setEnabled] = useState(true);

  const openForm = () => setIsFormOpen(true);
  const closeForm = () => setIsFormOpen(false);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window?.innerWidth < 1024);
    window?.addEventListener('resize', handleResize);
    return () => window?.removeEventListener('resize', handleResize);
  }, []);

  const selectPackage = (tier: (typeof tiers)[0] | typeof vipTier) => {
    setSelectedPackage(tier);
    openForm();
  };

  const toggleFeatures = () => {
    setShowAllFeatures(!showAllFeatures);
  };

  useEffect(() => {
    const idFromHash = window?.document.location.hash.slice(1);
    if (id === idFromHash) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [id]);

  useEffect(() => {
    console.log(frequency);
  }, [frequency]);

  return (
    <>
      <div id={id} data-meta="PriceBlockNMT" className="py-14">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <CustomRichText
              data={title}
              tag="div"
              className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl"
            />
          </div>
          <CustomRichText
            data={subtitle}
            tag="div"
            className="mx-auto mt-6 max-w-7xl text-center text-md leading-8 text-white"
          />
          <div className="mt-8 lg:flex justify-center max-lg:w-full lg:min-w-max">
            <fieldset aria-label="Payment frequency">
              {isSmallScreen ? (
                <div className="max-w-md mx-auto">
                  <div className="grid grid-cols-2 gap-2">
                    {frequencyOptions.map(option => (
                      <button
                        key={option.value}
                        onClick={() => setFrequency(option)}
                        className={`flex justify-between items-center px-6 py-3 rounded-full shadow-md ${
                          frequency.value === option.value
                            ? 'bg-custom-btn-gradient-violet text-white'
                            : 'bg-white text-black'
                        }`}
                      >
                        <span className="text-sm uppercase font-semibold truncate">
                          {option.label}
                        </span>
                        {option.discount ? (
                          <span
                            className={classNames(
                              'rounded-full px-2 py-1 text-center text-sm',
                              frequency.value === option.value ? 'border-1 border-whi' : '',
                            )}
                          >
                            {option.discount}
                          </span>
                        ) : (
                          <ArrowDownRightIcon className="h-6 w-6" />
                        )}
                      </button>
                    ))}
                    {/* VIP */}
                    <button
                      key="vip"
                      onClick={() =>
                        setFrequency({
                          value: 'vip',
                          label: 'VIP',
                          priceSuffix: 'грн/урок',
                        })
                      }
                      className={`flex justify-between items-center px-6 py-3 rounded-full shadow-md ${
                        frequency.value === 'vip'
                          ? 'bg-custom-btn-gradient-violet text-white'
                          : 'bg-white text-black'
                      }`}
                    >
                      <span className="text-sm uppercase font-semibold truncate">VIP</span>
                    </button>
                  </div>
                </div>
              ) : (
                <RadioGroup
                  value={frequency}
                  onChange={setFrequency}
                  className="hidden bg-white lg:grid lg:items-center lg:grid-cols-4 gap-x-8 rounded-3xl lg:rounded-full p-1 font-semibold leading-5 ring-1 ring-inset ring-gray-200"
                >
                  {frequencyOptions.map(option => (
                    <Radio
                      key={option.value}
                      value={option}
                      className="cursor-pointer rounded-full py-2 text-md data-[checked]:bg-custom-btn-gradient-violet lg:text-gray-600"
                    >
                      <div
                        className={`${
                          frequency.value === option.value
                            ? 'px-8 bg-custom-btn-gradient-violet rounded-full text-white flex justify-between items-center'
                            : 'text-center'
                        } lg:min-w-[220px] flex-1`}
                      >
                        <span className="uppercase">{option.label}</span>
                        {option.discount ? (
                          <span
                            className={`${
                              frequency.value === option.value
                                ? 'border border-white rounded-full p-2'
                                : ''
                            } uppercase text-center text-white`}
                          >
                            {option.discount}
                          </span>
                        ) : (
                          <ArrowDownRightIcon
                            className={`${
                              frequency.value === option.value ? 'h-7 w-7 m-[5px]' : 'hidden'
                            } inline`}
                          />
                        )}
                      </div>
                    </Radio>
                  ))}
                  {/* VIP */}
                  <Radio
                    key="vip"
                    value={{
                      value: 'vip',
                      label: 'VIP',
                      priceSuffix: 'грн/урок',
                    }}
                    className={`cursor-pointer rounded-full py-2 text-md ${frequency.value === 'vip' ? 'bg-custom-btn-gradient-violet' : ''} lg:text-gray-600`}
                  >
                    <div
                      className={`${
                        frequency.value === 'vip'
                          ? 'px-8 bg-custom-btn-gradient-violet rounded-full text-white flex justify-between items-center'
                          : 'text-center'
                      } lg:min-w-[220px] flex-1`}
                    >
                      <span className="uppercase">VIP</span>
                      <ArrowDownRightIcon
                        className={`${
                          frequency.value === 'vip' ? 'h-7 w-7 m-[5px]' : 'hidden'
                        } inline`}
                      />
                    </div>
                  </Radio>
                </RadioGroup>
              )}
              <Field className="flex items-center justify-center mt-4">
                <Switch
                  checked={enabled}
                  onChange={setEnabled}
                  className="group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none ring-2 ring-violet-600 ring-offset-2 data-[checked]:bg-violet-600"
                >
                  <span
                    aria-hidden="true"
                    className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
                  />
                </Switch>
                <Label as="span" className="ml-3 text-sm">
                  <span className="font-medium text-white">{individualLessonsLabel}</span>
                </Label>
              </Field>
            </fieldset>
          </div>
          <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-4 lg:mx-0 lg:max-w-none lg:grid-cols-4">
            {frequency.value === 'vip' ? (
              <div
                key="vip"
                className="rounded-3xl px-4 py-8 justify-between bg-white col-start-2 col-end-4"
              >
                <div className="flex lg:flex-col max-lg:gap-2 max-lg:justify-between">
                  <div className="flex items-center justify-between gap-x-4">
                    <p
                      id={vipTier.id}
                      className="uppercase text-white rounded-full py-2 px-4 bg-custom-btn-gradient-violet text-sm font-semibold lg:leading-6"
                    >
                      {vipTier.name}
                    </p>
                  </div>
                  <p className="lg:mt-6 flex items-baseline gap-x-1">
                    <span className="text-xl lg:text-4xl font-bold tracking-tight text-gray-900">
                      {enabled ? vipTier.priceWithPersonal : vipTier.price}
                    </span>
                    <span className="text-sm font-semibold leading-6 text-gray-900">
                      {frequency.priceSuffix}
                    </span>
                  </p>
                  <p className="hidden lg:block">
                    {enabled ? (
                      <CustomRichText data={vipTier.descriptionWithPersonal} tag="span" />
                    ) : (
                      <CustomRichText data={vipTier.description} tag="span" />
                    )}
                  </p>
                </div>
                <div className="border-b-2 pb-4 border-b-violet-200">
                  <button
                    aria-describedby={vipTier.id}
                    onClick={() => {
                      selectPackage(vipTier);
                    }}
                    className={`${
                      vipTier.superPrice ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-900'
                    } hidden uppercase mt-6 lg:flex items-center justify-center w-full ring-1 ring-inset ring-violet-500 rounded-full px-1 py-2 text-sm font-semibold leading-6 relative`}
                  >
                    <CustomRichText data={selectPackageLabel} tag="span" className="" />
                    <div className="absolute right-1 flex items-center">
                      <div className="rounded-full custom-gradient-violet-btn">
                        <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                      </div>
                    </div>
                  </button>
                </div>
                <ul className="hidden lg:block mt-8 space-y-3 text-sm leading-6 text-gray-600 xl:mt-10 transition-all min-h-44">
                  {(enabled ? vipTier.featuresWithPersonal : vipTier.features).map(
                    (feature, index) => (
                      <li key={vipTier.id + index} className="flex gap-x-3">
                        <CheckIcon
                          aria-hidden="true"
                          className={`${
                            feature.vip.enters
                              ? 'text-white custom-gradient-violet-btn'
                              : 'bg-violet-600/40'
                          } h-6 w-6 flex-none text-white rounded-full`}
                        />
                        <CustomRichText
                          data={feature.vip.name}
                          tag="span"
                          className={feature.vip.enters ? 'text-gray-900' : 'text-gray-300'}
                        />
                      </li>
                    ),
                  )}
                </ul>
                <button
                  aria-describedby={vipTier.id}
                  onClick={() => setSelectedTier(vipTier)}
                  className={`${
                    vipTier.superPrice ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-900'
                  } hidden uppercase mt-6 max-lg:flex items-center justify-center w-full ring-1 ring-inset ring-violet-500 rounded-full px-1 py-2 text-sm font-semibold leading-6 relative`}
                >
                  <span>{viewPackageLabel}</span>
                  <div className="absolute right-1 flex items-center">
                    <div className="rounded-full custom-gradient-violet-btn">
                      <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                    </div>
                  </div>
                </button>
              </div>
            ) : (
              dispayedTiers.map(tier => (
                <div
                  key={tier.id}
                  className={`${
                    tier.superPrice ? 'ring-2 ring-indigo-600' : 'ring-1 ring-gray-200'
                  } rounded-3xl px-4 py-8 justify-between bg-white`}
                >
                  <div className="flex lg:flex-col max-lg:gap-2 max-lg:justify-between">
                    <div className="flex items-center justify-between gap-x-4">
                      <p
                        id={tier.id}
                        className="uppercase text-white rounded-full py-2 px-4 bg-custom-btn-gradient-violet text-sm font-semibold lg:leading-6"
                      >
                        {tier.name}
                      </p>
                    </div>
                    <p className="lg:mt-6 flex items-baseline gap-x-1">
                      <span className="text-xl lg:text-4xl font-bold tracking-tight text-gray-900">
                        {enabled
                          ? tier.priceWithPersonal[frequency.value as FrequencyType]
                          : tier.price[frequency.value as FrequencyType]}
                      </span>
                      <span className="text-sm font-semibold leading-6 text-gray-900">
                        {frequency.priceSuffix}
                      </span>
                    </p>
                    <p className="hidden lg:block">
                      {enabled ? (
                        <CustomRichText
                          data={tier.descriptionWithPersonal[frequency.value as FrequencyType]}
                          tag="span"
                        />
                      ) : (
                        <CustomRichText
                          data={tier.description[frequency.value as FrequencyType]}
                          tag="span"
                        />
                      )}
                    </p>
                  </div>
                  <div className="border-b-2 pb-4 border-b-violet-200">
                    <button
                      aria-describedby={tier.id}
                      onClick={() => {
                        selectPackage(tier);
                      }}
                      className={`${
                        tier.superPrice ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-900'
                      } hidden uppercase mt-6 lg:flex items-center justify-center w-full ring-1 ring-inset ring-violet-500 rounded-full px-1 py-2 text-sm font-semibold leading-6 relative`}
                    >
                      <CustomRichText data={selectPackageLabel} tag="span" className="" />
                      <div className="absolute right-1 flex items-center">
                        <div className="rounded-full custom-gradient-violet-btn">
                          <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                        </div>
                      </div>
                    </button>
                  </div>
                  <ul className="hidden lg:block mt-8 space-y-3 text-sm leading-6 text-gray-600 xl:mt-10 transition-all min-h-44">
                    {frequency.value === 'vip' ? (
                      <div>vip</div>
                    ) : (
                      (enabled ? tier.featuresWithPersonal : tier.features).map(
                        (feature, index) => (
                          <li key={tier.id + index} className="flex gap-x-3">
                            <CheckIcon
                              aria-hidden="true"
                              className={`${
                                feature[frequency.value as FrequencyType]?.enters
                                  ? 'text-white custom-gradient-violet-btn'
                                  : 'bg-violet-600/40'
                              } h-6 w-6 flex-none text-white rounded-full`}
                            />
                            <CustomRichText
                              data={feature[frequency.value as FrequencyType]?.name}
                              tag="span"
                              className={
                                feature[frequency.value as FrequencyType]?.enters
                                  ? 'text-gray-900'
                                  : 'text-gray-300'
                              }
                            />
                          </li>
                        ),
                      )
                    )}
                  </ul>
                  <button
                    aria-describedby={tier.id}
                    onClick={() => setSelectedTier(tier)}
                    className={`${
                      tier.superPrice ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-900'
                    } hidden uppercase mt-6 max-lg:flex items-center justify-center w-full ring-1 ring-inset ring-violet-500 rounded-full px-1 py-2 text-sm font-semibold leading-6 relative`}
                  >
                    <span>{viewPackageLabel}</span>
                    <div className="absolute right-1 flex items-center">
                      <div className="rounded-full custom-gradient-violet-btn">
                        <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                      </div>
                    </div>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      {selectedTier && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto w-full h-lvh">
          <div className="bg-white px-4 rounded-xl">
            <div className="w-full flex justify-end pt-4">
              <button onClick={() => setSelectedTier(null)} aria-label="Закрити">
                <XCircleIcon className="h-8 w-8 text-violet-600" />
              </button>
            </div>
            <div className="p-4 lg:p-8 xl:p-10 justify-between bg-white">
              <div className="flex lg:flex-col max-lg:gap-2 max-lg:justify-between">
                <div className="flex items-center justify-between gap-x-4">
                  <p
                    id={selectedTier.id}
                    className="uppercase text-white rounded-full py-1 px-2 bg-custom-btn-gradient-violet text-base font-semibold lg:leading-8"
                  >
                    {selectedTier.name}
                  </p>
                </div>
                <p className="lg:mt-6 flex items-baseline gap-x-1">
                  <span className="text-xl lg:text-4xl font-bold tracking-tight text-gray-900">
                    {enabled
                      ? typeof selectedTier.priceWithPersonal === 'object'
                        ? selectedTier.priceWithPersonal[frequency.value as FrequencyType]
                        : selectedTier.priceWithPersonal
                      : typeof selectedTier.price === 'object'
                        ? selectedTier.price[frequency.value as FrequencyType]
                        : selectedTier.price}
                  </span>
                  <span className="text-sm font-semibold leading-6 text-gray-600">
                    {frequency.priceSuffix}
                  </span>
                </p>
              </div>
              <p className="mt-4">
                {enabled ? (
                  <CustomRichText
                    data={
                      typeof selectedTier.descriptionWithPersonal === 'object'
                        ? selectedTier.descriptionWithPersonal[frequency.value]
                        : selectedTier.descriptionWithPersonal
                    }
                    tag="span"
                  />
                ) : (
                  <CustomRichText
                    data={
                      typeof selectedTier.description === 'object'
                        ? selectedTier.description[frequency.value]
                        : selectedTier.description
                    }
                    tag="span"
                  />
                )}
              </p>
              <button
                aria-describedby={selectedTier.id}
                onClick={() => selectPackage(selectedTier)}
                className={`${
                  selectedTier.superPrice ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-900'
                } uppercase mt-6 lg:flex items-center justify-center w-full ring-1 ring-inset ring-violet-500 rounded-full px-1 py-2 text-sm font-semibold leading-6 relative`}
              >
                <span>{selectPackageLabel}</span>
                <div className="absolute top-1 right-1 flex items-center">
                  <div className="rounded-full custom-gradient-violet-btn">
                    <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                  </div>
                </div>
              </button>
              <ul className="mt-8 space-y-3 text-sm leading-6 text-gray-600 xl:mt-10 transition-all">
                {(enabled ? selectedTier.featuresWithPersonal : selectedTier.features).map(
                  (feature, index) => (
                    <li key={selectedTier.id + index} className="flex gap-x-3">
                      <CheckIcon
                        aria-hidden="true"
                        className={`${
                          (feature as Record<string, { name: ReactNode; enters: boolean }>)[
                            frequency.value
                          ].enters
                            ? 'text-white custom-gradient-violet-btn'
                            : 'bg-violet-600/40'
                        } h-6 w-6 flex-none text-white rounded-full`}
                      />
                      <CustomRichText
                        data={
                          (feature as Record<string, { name: ReactNode; enters: boolean }>)[
                            frequency.value
                          ].name
                        }
                        tag="span"
                        className={
                          (feature as Record<string, { name: ReactNode; enters: boolean }>)[
                            frequency.value
                          ].enters
                            ? 'text-gray-900'
                            : 'text-gray-300'
                        }
                      />
                    </li>
                  ),
                )}
              </ul>
              <button
                onClick={() => setSelectedTier(null)}
                aria-describedby={selectedTier.id}
                className={`${
                  selectedTier.superPrice ? 'bg-gray-600 text-white shadow-sm' : 'text-gray-600'
                } uppercase mt-6 lg:flex items-center justify-center w-full rounded-full px-1 py-2 text-sm font-semibold leading-6`}
              >
                <span>{collapseLabel}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {isFormOpen && selectedPackage && (
        <ModalForm
          closeForm={closeForm}
          btnMeta={{
            tag: `${modalTagPrefix}-${selectedPackage?.name}-${frequency.label}`,
          }}
          color={'violet'}
        />
      )}
    </>
  );
}
